import { fetchPublicSearch, ImageMetadata, PublicFacet } from './api';
import { adaptPublicImage, publicUrl } from './publicRecords';
import { toEpochMs } from '../utils/time';

type DiscoveryGroup = 'character' | 'cast' | 'pair' | 'style' | 'signature' | 'scene' | 'theme';
const DISCOVERY_GROUPS: readonly (readonly DiscoveryGroup[])[] = [['character'], ['cast', 'pair'], ['style', 'signature'], ['scene'], ['theme']];
export function facetSearchUrl(group: string, slug: string): string {
    return '/?' + new URLSearchParams({ page: 'search', [group]: slug });
}
export function relatedFacets(facets: Record<string, PublicFacet>) {
    return DISCOVERY_GROUPS.flatMap(keys => {
        const group = keys.find(key => facets[key]?.slug && facets[key]?.label);
        return group ? [{ group, ...facets[group] }] : [];
    });
}
export interface DiscoveryEntry { group: DiscoveryGroup; slug: string; label: string; image: ImageMetadata; }
function matchesGroup(image: ImageMetadata, group: string, slug: string): boolean {
    return group === 'character'
        ? image.characters?.some(character => character.slug === slug) === true
        : image.facets?.[group]?.slug === slug;
}
const relatedCache = new Map<string, { at: number; promise: Promise<ImageMetadata[]> }>();
export function fetchRelatedThumbnails(group: string, slug: string): Promise<ImageMetadata[]> {
    const key = `${group}:${slug}`;
    const cached = relatedCache.get(key);
    if (cached && Date.now() - cached.at < 60000) return cached.promise;
    const promise = fetchPublicSearch(new URLSearchParams({ [group]: slug, per_page: '4' })).then(result => {
        const seen = new Set<number>();
        return (result.items || []).filter(image => {
            if (!matchesGroup(image, group, slug) || !publicUrl(image.thumb) || seen.has(image.id) ||
                !Number.isSafeInteger(image.id) || image.id <= 0 || !/^[a-zA-Z0-9_-]+$/.test(image.hash || '') ||
                image.is_published === 0 || (image.is_discard !== undefined && image.is_discard !== 0) || image.reviewed_at === null ||
                (toEpochMs(image.published_at) ?? 0) > Date.now()) return false;
            seen.add(image.id);
            return true;
        }).slice(0, 4);
    });
    relatedCache.delete(key);
    if (relatedCache.size >= 64) relatedCache.delete(relatedCache.keys().next().value!);
    relatedCache.set(key, { at: Date.now(), promise });
    promise.catch(() => { if (relatedCache.get(key)?.promise === promise) relatedCache.delete(key); });
    return promise;
}
let cached: { at: number; promise: Promise<DiscoveryEntry[]> } | null = null;
export function fetchDiscovery(): Promise<DiscoveryEntry[]> {
    if (cached && Date.now() - cached.at < 60000) return cached.promise;
    const promise = (async () => {
        const index = await fetchPublicSearch(new URLSearchParams({ per_page: '1' }));
        // One stable entry per public group; no popularity ranking.
        const candidates = DISCOVERY_GROUPS.flatMap(keys => {
            const group = keys.find(key => index.groups?.[key]?.values.some(value => value.count > 0));
            if (!group) return [];
            const value = [...(index.groups?.[group]?.values || [])].filter(value => value.count > 0).sort((a, b) => a.slug.localeCompare(b.slug))[0];
            return value ? [{ group, slug: value.slug, label: value.label }] : [];
        });
        const results = await Promise.allSettled(candidates.map(async candidate => {
            const result = await fetchPublicSearch(new URLSearchParams({ [candidate.group]: candidate.slug, per_page: '1' }));
            const image = result.items?.find(image => matchesGroup(image, candidate.group, candidate.slug) && adaptPublicImage(image) && image.thumb);
            return image ? { ...candidate, image } : null;
        }));
        return results.flatMap(result => result.status === 'fulfilled' && result.value ? [result.value] : []);
    })();
    cached = { at: Date.now(), promise };
    promise.catch(() => { if (cached?.promise === promise) cached = null; });
    return promise;
}
